// Adapted from apps/admin/src/pages/dashboard/gps-tracking/components/vehicle-card.tsx (PRD #875):
// the organization and the job summary take the place of the drivers.
import React, { memo, useCallback } from "react";
import { cn } from "@racoon/ui";
import { Namespace, useLang, useT } from "@racoon/i18n";
import type { FleetTruck, JobSummary } from "../model/fleet";

interface VehicleCardProps {
  vehicle: FleetTruck;
  isSelected: boolean;
  onSelect: (vehicleId: string) => void;
}

const routesKey = (count: number, lang: string) => {
  const form = new Intl.PluralRules(lang).select(count);
  return form === "one"
    ? "gpsTracking.routesOne"
    : form === "few"
      ? "gpsTracking.routesFew"
      : "gpsTracking.routesMany";
};

/** Red while a job failed, green once all are done, blue while work goes on. */
const summaryDotColor = ({ done, notDone, total }: JobSummary) =>
  notDone > 0
    ? "bg-brand-red"
    : total > 0 && done === total
      ? "bg-brand-green"
      : total > 0
        ? "bg-brand-blue"
        : "bg-gray-300";

const VehicleCardComponent: React.FC<VehicleCardProps> = ({ vehicle, isSelected, onSelect }) => {
  const t = useT();
  const { lang } = useLang();
  const { truck, organizationName, routeCount, summary } = vehicle;

  const handleCardClick = useCallback(() => {
    onSelect(vehicle.id);
  }, [onSelect, vehicle.id]);

  return (
    <div
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleCardClick();
        }
      }}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      data-testid="gps-tracking-vehicle"
      data-truck-id={vehicle.id}
      className={cn(
        "p-4 rounded-lg cursor-pointer transition-all border",
        isSelected
          ? "border-brand-blue border-2"
          : "border-transparent hover:border-gray-300 hover:shadow-sm",
      )}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className={cn("w-3 h-3 shrink-0 rounded-full", summaryDotColor(summary))} />
        <div className="flex flex-1 flex-row justify-between gap-2 min-w-0">
          <span className="inline-flex items-center rounded-md text-sm border border-text-primary px-1.5 py-0.5 font-medium">
            {truck.plateNumber}
          </span>
          <span className="truncate text-sm text-gray-700">{truck.model}</span>
        </div>
      </div>

      <div className="ml-5 space-y-1">
        {organizationName && (
          <div className="truncate text-sm font-medium text-slate-900">{organizationName}</div>
        )}
        <div data-testid="gps-tracking-vehicle-summary" className="text-xs text-text-secondary">
          {t(Namespace.Govdash, routesKey(routeCount, lang), { count: routeCount })}
          {" · "}
          {t(Namespace.Govdash, "gpsTracking.doneOfTotal", {
            done: summary.done,
            total: summary.total,
          })}
        </div>
        {(summary.notDone > 0 || summary.inProgress > 0) && (
          <div className="flex flex-wrap gap-x-3 text-xs">
            {summary.notDone > 0 && (
              <span data-testid="gps-tracking-vehicle-not-done" className="text-brand-red">
                {t(Namespace.Govdash, "gpsTracking.notDone", { count: summary.notDone })}
              </span>
            )}
            {summary.inProgress > 0 && (
              <span data-testid="gps-tracking-vehicle-in-progress" className="text-brand-blue">
                {t(Namespace.Govdash, "gpsTracking.inProgress", { count: summary.inProgress })}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const VehicleCard = memo(VehicleCardComponent);
